<template>
  <v-content class="mt-0 pt-0">
  <v-container fluid>
    <pr-loading v-model="loading" />
    <v-snackbar
      v-model="showMessage"
      value="true"
      right top
      multi-line
      color="info"
      :timeout="2000"
    >
      {{ $t('Not implemented') }}
      <v-btn
        dark
        flat
        @click="showMessage = false"
      >
        {{ $t('OK')}}
      </v-btn>
    </v-snackbar>

    <v-toolbar flat color="white" class="elevation-1">
      <v-toolbar-title>{{ $t('cod report screen') }}</v-toolbar-title>
      <v-divider
        class="mx-2"
        inset
        vertical
        :extended="$vuetify.breakpoint.xsOnly"
      ></v-divider>

      <template v-slot:extension v-if="$vuetify.breakpoint.xsOnly">
        <v-btn-toggle v-model="dataFilter" @change="dateFilterChanged">
          <v-tooltip bottom v-for="(type,idx) in dataFilterTypes" :key="idx">
            <v-btn slot="activator" :value="type" flat>{{ type }}</v-btn>
            <span>{{ $t('Tooltip.dataFilter.' + type) }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" value="custom" flat>C</v-btn>
            <span>{{ $t('Tooltip.dataFilter.custom') }}</span>
          </v-tooltip>
        </v-btn-toggle>

        <template v-if="dataFilter === 'custom'">
          <v-input-date-picker v-model="dateSince" @input="dateFilterChanged" :label="$t('Date from')" hide-details clearable class="mx-2"></v-input-date-picker>
          <v-input-date-picker v-model="dateUntil" @input="dateFilterChanged" :label="$t('Date to'  )" hide-details clearable class="mx-2"></v-input-date-picker>
        </template>
      </template>
      <template v-if="!$vuetify.breakpoint.xsOnly">
        <v-btn-toggle v-model="dataFilter" @change="dateFilterChanged">
          <v-tooltip bottom v-for="(type,idx) in dataFilterTypes" :key="idx">
            <v-btn slot="activator" :value="type" flat>{{ type }}</v-btn>
            <span>{{ $t('Tooltip.dataFilter.' + type) }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" value="custom" flat>C</v-btn>
            <span>{{ $t('Tooltip.dataFilter.custom') }}</span>
          </v-tooltip>
        </v-btn-toggle>

        <template v-if="dataFilter === 'custom'">
          <v-input-date-picker v-model="dateSince" @input="dateFilterChanged" :label="$t('Date from')" hide-details clearable class="mx-2"></v-input-date-picker>
          <v-input-date-picker v-model="dateUntil" @input="dateFilterChanged" :label="$t('Date to'  )" hide-details clearable class="mx-2"></v-input-date-picker>
        </template>
      </template>

      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="exportExcel" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcel">
          <v-icon class="text--secondary">far fa-file-excel</v-icon>
        </v-btn>
        <span>{{ $t('Export to Excel') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="exportPdf" v-shortkey="['ctrl', 'alt', 'p']" @shortkey="exportPdf">
          <v-icon class="text--secondary">far fa-file-pdf</v-icon>
        </v-btn>
        <span>{{ $t('Export to PDF') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-menu slot="activator"
          :close-on-content-click="false"
          :nudge-width="200"
          offset-x
        >
          <v-btn icon slot="activator">
            <v-icon class="text--secondary">settings</v-icon>
          </v-btn>

          <v-card>
            <v-list>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>

            <v-divider></v-divider>

            <v-list>
              <v-list-tile v-for="(header,key) in headers" :key="key" v-if="header.value != 'actions'">
                <v-list-tile-action>
                  <v-switch v-model="header.show" color="purple" :disabled="columnHideCheck(header)" @change="storeFilter"></v-switch>
                </v-list-tile-action>
                <v-list-tile-title>{{ header.text }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-menu>
        <span>{{ $t('Table settings') }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-layout row wrap>
      <v-flex xs12>
        <v-data-table
          :headers="headers"
          :items="data"
          :pagination.sync="pagination"
          :total-items="totalItems"
          :loading="loading"
          :rows-per-page-items="[5, 10, 25]"
          :rows-per-page-text="$t('Rows per page')"
          class="elevation-1"
        >
          <template slot="headers" slot-scope="props">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.text"
                v-if="header.show"
                :class="[
                  header.sortable ? 'column sortable' : '',
                  pagination.descending ? 'desc' : 'asc',
                  header.value === pagination.sortBy ? 'active' : ''
                ]"
                @click="header.sortable && changeSort(header.value)"
              >
                <v-icon small v-if="header.sortable">arrow_upward</v-icon>
                <v-tooltip bottom>
                  <span slot="activator">{{ header.text }}</span>
                  <span>{{ header.text }}</span>
                </v-tooltip>
              </th>
            </tr>
          </template>
          <template slot="items" slot-scope="props">
            <td v-for="(header,idx) in headers" :key="idx" v-if="header.show && header.value !== 'actions'" class="text-xs-left">{{ getObjectValue(props.item, idx) }}</td>
          </template>
        </v-data-table>
        <div class="text-xs-center pt-2">
          <v-pagination v-model="pagination.page" :length="pages" circle total-visible="7"></v-pagination>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
  </v-content>
</template>

<script>
import _object from "lodash/object";
import { cod } from "../resources";
import { downloadFile } from "../components/util";
import VInputDatePicker from "@/components/VInputDatePicker";
import PrLoading from '@/components/Loading';
import { mapGetters, mapActions } from "vuex";

export default {
  components : {
    PrLoading,
    VInputDatePicker
  },
  data () {
    return {
      showMessage: false,
      totalItems: 0,
      data: [],
      search : '',
      loading: false,
      pagination: {},

      dataFilter : '30',
      dataFilterTypes : [ '30', '60', '90' ],

      dateSince : null,
      dateUntil : null,

      headers: [
        { text: this.$t('Date'),            value: 'date',            align: 'left',  sortable: true,  show: true, type: 'date', },
        { text: this.$t('Manifest'),        value: 'manifest',        align: 'left',  sortable: false, show: true },
        { text: this.$t('Reference'),       value: 'reference',       align: 'left',  sortable: false, show: true },
        { text: this.$t('Target Partner'),  value: 'recipient',       align: 'left',  sortable: false, show: true },
        { text: this.$t('Settlement'),      value: 'settlement',      align: 'left',  sortable: false, show: true },
        { text: this.$t('COD Amount'),      value: 'amount',          align: 'right', sortable: false, show: true },
        { text: this.$t('COD Paid'),        value: 'paid',            align: 'left',  sortable: false, show: true },
        { text: this.$t('Transfer Date'),   value: 'transfer.date',   align: 'left',  sortable: false, show: true, type: 'date', },
        { text: this.$t('Transfer Type'),   value: 'transfer.type',   align: 'left',  sortable: false, show: true, type: 'enum', enum: 'Transfer' },
        { text: this.$t('Transfer Amount'), value: 'transfer.amount', align: 'right', sortable: false, show: true },
        { text: this.$t('Transfer Paid'),   value: 'transfer.paid',   align: 'right', sortable: false, show: true }
      ]
    }
  },

  computed : {
    ...mapGetters(['codReportFilter']),
    pages () {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    }
  },

  watch: {
    pagination: {
      handler () {
        this.listData();
      },
      deep: true
    }
  },

  mounted () {
    let codReportFilter = null;
    try {
      codReportFilter = JSON.parse(this.codReportFilter);
    } catch(e) {
      codReportFilter = null;
    }

    if (codReportFilter) {
      this.dataFilter = codReportFilter.dataFilter;
      this.dateSince = codReportFilter.dateSince;
      this.dateUntil = codReportFilter.dateUntil;
      if (codReportFilter.pagination)
        this.pagination = codReportFilter.pagination;
      for (let i = 0; i < codReportFilter.shown.length; i++)
        this.headers[i].show = codReportFilter.shown[i];

    } else {
      this.dateSince = new Date().setDate(new Date().getDate() - 120);
      this.dateUntil = new Date().valueOf();
    }

    this.getDataFromApi().then(data => {
        this.data = data.items;
        this.totalItems = data.total;
      }, () => {
        console.log('mounted loading rejected');
      });
  },

  methods: {
    ...mapActions(['updateCodReportFilter']),

    getObjectValue(obj, idx) {
      let v = _object.get(obj, this.headers[idx].value);
      let t = this.headers[idx].type;
      if (t === 'date')
        return v ? new Date(v).toLocaleDateString() : '';
      if (t === 'enum' && v) {
        let e = this.headers[idx].enum;
        if (!e)
          e = "Enum";
        return this.$t(e + '.' + v);
      }

      let text = this.headers[idx].text;
      if (
          (text === this.$t('COD Amount') 
          || text === this.$t('Transfer Amount')
          || text === this.$t('Transfer Paid')
          ) 
          && v) 
        return v.toFixed(2) + ' €';

      if (typeof v === 'boolean')
        return this.$t('Boolean.' + v);
      if (typeof v === 'number')
        return v.toLocaleString();
      return v;
    },

    listData() {
      this.storeFilter();
      this.getDataFromApi().then(data => {
          this.data = data.items;
          this.totalItems = data.total;
        }, () => {
          console.log('loading rejected');
        });
    },

    getDataFromApi () {
      if (this.loading)
        return new Promise((resolve, reject) => {
          reject();
        });

      return new Promise((resolve, reject) => {
        this.loading = true;
        const { sortBy, descending, page, rowsPerPage } = this.pagination;

        let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
        let length = (rowsPerPage>=1) ? rowsPerPage : -1;
        let sort   = (sortBy) ? (descending ? "-" : "") + sortBy : "";
        let since = this.dateSince;
        let until = this.dateUntil;

        // check filtering
        let d = Number(this.dataFilter);
        if (isNaN(d)) {
          // custom date filter
        } else {
          until = new Date();
          since = new Date().setDate( until.getDate() - d );
          until = until.valueOf();
        }

        cod.report({ since, until, length, offset, sort }).then(response => {
          const items = response.body.result;
          const total = response.body.length;
          resolve({
            items,
            total
          });
        }, response => {
          console.log("Error " + response.status);
        }).finally(() => {
          this.loading = false;
        });
      });
    },

    columnHideCheck(header) {
      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },

    changeSort(column) {
      console.log("change sort of item #" + column);
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    dateFilterChanged() {
      if (!this.dataFilter)
        return;
      this.listData();
    },

    storeFilter() {
      let a = new Array();
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value !== 'actions')
          a.push(this.headers[i].show);

      let s = {
        dateSince  : this.dateSince,
        dateUntil  : this.dateUntil,
        dataFilter : this.dataFilter,
        pagination : this.pagination,
        shown      : a
      };
      this.updateCodReportFilter(JSON.stringify(s));
    },

    exportPdf() {
      this.msgType = "info";
      this.msgText = this.$t("Not implemented");
      this.showMessage = true;
    },

    exportExcel() {
      if (this.loading)
        return;

      this.loading = true;
      let since = this.dateSince;
      let until = this.dateUntil;

      // check filtering
      let d = Number(this.dataFilter);
      if (isNaN(d)) {
        // custom date filter
      } else {
        until = new Date();
        since = new Date().setDate( until.getDate() - d );
        until = until.valueOf();
      }
      cod.xls({ since, until }).then(response => {
        downloadFile(response);
      }, error => {
        this.msgType = "error";
        this.msgText = error.statusText;
        this.showMessage = true;
      }).finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>

<style scoped>
</style>